
import React from 'react'
import PropTypes from 'prop-types'
import Robox from 'robox'
import ruled from 'ruled'
import config from './config'

const getWidth = (props) => (matches = []) => {
  return matches.reduce((a, b) => {
    return props[b] || a
  }, props.col || null)
}

const bgGrid = ruled()

const QUERIES = Object.create(null)

function getMatchMedia (query) {
  if (!(query in QUERIES)) {
    QUERIES[query] = window.matchMedia(query)
  }

  return QUERIES[query]
}

// The breakpoint keys that match right now. Read in the constructor too, so
// the first render already has its sm/md/lg width: starting from `col` and
// correcting in componentDidMount paints a col={12} md={6} box full width for
// a frame, and a chart inside measures that and snaps narrower after.
function currentMatches (breakpoints) {
  const matches = []
  for (let key in breakpoints) {
    if (getMatchMedia(breakpoints[key]).matches) {
      matches.push(key)
    }
  }
  return matches
}

// MediaQueryList.add/removeEventListener only exist in Safari 14+ (Sept 2020).
// iOS Safari <= 13 only has the legacy add/removeListener, so feature-detect to
// avoid "addEventListener is not a function" TypeErrors on old WebKit.
function addMqlListener (mql, fn) {
  if (mql.addEventListener) {
    mql.addEventListener('change', fn)
  } else {
    mql.addListener(fn)
  }
}

function removeMqlListener (mql, fn) {
  if (mql.removeEventListener) {
    mql.removeEventListener('change', fn)
  } else {
    mql.removeListener(fn)
  }
}

const withReflex = ({
  listen = true
} = {}) => Comp => {
  const Base = Robox(Comp)

  class ReflexWrap extends React.Component {
    constructor () {
      super()

      this.getBreakpoints = () => {
        return config.breakpoints
      }

      this.state = {
        matches: currentMatches(this.getBreakpoints())
      }

      this.match = () => {
        if (this._unmounted) {
          return
        }

        this.setState({ matches: currentMatches(this.getBreakpoints()) })
      }
    }

    componentDidMount () {
      const breakpoints = this.getBreakpoints()

      if (listen) {
        for (let key in breakpoints) {
          addMqlListener(getMatchMedia(breakpoints[key]), this.match)
        }
      }
    }

    componentWillUnmount () {
      this._unmounted = true

      const breakpoints = this.getBreakpoints()
      for (let key in breakpoints) {
        removeMqlListener(getMatchMedia(breakpoints[key]), this.match)
      }
    }

    render () {
      const {
        debug,
        style,
        ...props
      } = this.props
      const { matches } = this.state
      const breakpoints = this.getBreakpoints()
      const grid = debug || false

      Object.keys(breakpoints).forEach((key) => {
        delete props[key]
      })

      const width = getWidth(this.props)(matches)

      const sx = grid ? {
        backgroundImage: bgGrid,
        backgroundSize: '8px 8px',
        ...style
      } : (style || null)

      // Map legacy props
      if (props.column) {
        props.flexColumn = props.column
        delete props.column
      }

      if (props.auto) {
        props.flexAuto = props.auto
        delete props.auto
      }

      return (
        <Base
          {...props}
          col={width}
          style={sx} />
      )
    }
  }

  ReflexWrap.propTypes = {
    flex: PropTypes.bool,
    wrap: PropTypes.bool,
    flexColumn: PropTypes.bool,
    column: PropTypes.bool,
    align: PropTypes.oneOf([
      'stretch',
      'center',
      'baseline',
      'flex-start',
      'flex-end'
    ]),
    justify: PropTypes.oneOf([
      'center',
      'space-around',
      'space-between',
      'flex-start',
      'flex-end'
    ]),
    flexAuto: PropTypes.bool,
    auto: PropTypes.bool,
    flexNone: PropTypes.bool,
    order: PropTypes.number,
    col: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    is: (props, propName, componentName) => {
      if (props[propName]) {
        return new Error(
          'Warning! Deprecated prop `' + propName + '` supplied to' +
            ' `' + componentName + '`. Use the Reflex higher order component instead. https://github.com/jxnblk/reflexbox/'
        )
      }
    }
  }

  return ReflexWrap
}

export default withReflex
